import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import type { Notification } from '@leanopx/shared';
import { api } from '@/lib/api';

/**
 * Shared TanStack Query infrastructure for the notifications surface
 * (issue #155, hardening per PR #156 review).
 *
 * The bell-dropdown fetches `limit: 5` and the `/notifications` page
 * fetches the default `limit: 50`. Originally both used the same
 * `['notifications', orgId]` key — the first mount won the cache, and
 * the other surface rendered the wrong row count until `staleTime`
 * elapsed (cache poisoning).
 *
 * Fix: each surface uses a limit-tagged key. The mark-read mutation
 * lives here so a click on either surface invalidates BOTH cache
 * entries via `setQueriesData` with the prefix, keeping the "marking
 * read here flips the row everywhere" UX without sharing a single
 * misshapen cache row.
 */

/**
 * Compose the canonical query key for a notifications query. Always
 * `['notifications', orgId, limit]` so a mark-read mutation can target
 * the right set of cache entries via the `['notifications', orgId]`
 * prefix.
 */
export function notificationsQueryKey(orgId: string, limit: number): QueryKey {
  return ['notifications', orgId, limit];
}

/**
 * Hook: returns a `mutate(id)` that PATCHes a notification to read=true.
 *
 * Cache strategy:
 *   - **onMutate** — optimistic flip across every cache entry under
 *     the `['notifications', orgId]` prefix (both the bell's
 *     `[…, 5]` cache and the page's `[…, 50]` cache, plus any future
 *     limit-keyed caller). Snapshots all affected caches so a server
 *     error can roll back cleanly.
 *   - **onError** — restore the snapshots. The optimistic flip is
 *     visible until then, but the rollback runs before the next
 *     render cycle so the user sees the row revert to unread.
 *   - **onSettled** — invalidate the same prefix so every affected
 *     query refetches once the mutation lands. Belt + suspenders on
 *     the optimistic flip: guarantees cache + server reconcile even
 *     if some subtle state divergence slipped past the optimistic
 *     update (e.g. a parallel write modified another field).
 */
export function useMarkNotificationRead(orgId: string): (id: string) => void {
  const queryClient = useQueryClient();
  const prefix: QueryKey = ['notifications', orgId];

  const mut = useMutation({
    mutationFn: (id: string) => api.markNotificationRead(orgId, id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: prefix });
      // Snapshot every matching cache so we can roll back if needed.
      const snapshots = queryClient.getQueriesData<{ notifications: Notification[] }>({
        queryKey: prefix,
      });
      // Optimistic flip across every (['notifications', orgId, limit])
      // entry that contains the row.
      queryClient.setQueriesData<{ notifications: Notification[] } | undefined>(
        { queryKey: prefix },
        (prev) =>
          prev
            ? {
                notifications: prev.notifications.map((n) =>
                  n.id === id ? { ...n, read: true } : n,
                ),
              }
            : prev,
      );
      return { snapshots };
    },
    onError: (_err, _id, ctx) => {
      if (!ctx) return;
      for (const [key, data] of ctx.snapshots) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: prefix });
    },
  });

  return (id: string) => mut.mutate(id);
}
